import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SEOHead } from '../../components/seo/SEOHead';
import { FAQSchema, BreadcrumbSchema } from '../../components/seo/SchemaMarkup';
import { Breadcrumbs } from '../../components/seo/Breadcrumbs';
import { LatestArticles } from '../../components/blog/LatestArticles';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { Badge } from '../../components/ui/badge';
import { Crown, Star, Wine, Users, ArrowRight, Check } from 'lucide-react';

export default function VIPTableBookingJamshedpur() {
  const navigate = useNavigate();

  const faqs = [
    {
      question: "What is included in VIP table booking at Scratch Unleashed?",
      answer: "VIP table booking includes reserved premium seating, priority entry (skip the line), dedicated server for bottle service, complimentary mixers and garnishes, premium glassware, personalized service throughout the night, and the best views of the DJ booth and dance floor. Minimum spend varies by table location and night."
    },
    {
      question: "How much does VIP table booking cost?",
      answer: "VIP table pricing varies based on location, night, and group size. Standard VIP tables start from ₹20,000 minimum spend. Premium stage-side tables range from ₹35,000-₹50,000. Special event nights and guest DJ performances have higher minimum spends. Contact us for specific event pricing."
    },
    {
      question: "How many people can a VIP table accommodate?",
      answer: "Standard VIP tables accommodate 4-6 people comfortably. Larger VIP booths can host 8-12 people. For groups larger than 12, we can combine multiple tables or reserve our exclusive lounge area which accommodates up to 20 guests."
    },
    {
      question: "Can I choose which bottle brands are included?",
      answer: "Absolutely! Your minimum spend can be used toward any bottles from our premium menu, which includes top-shelf vodka, whiskey, rum, tequila, champagne, and specialty spirits. Our server will present the full menu upon arrival and help you select based on your preferences."
    },
    {
      question: "Do I need to book VIP tables in advance?",
      answer: "Advance booking is highly recommended, especially for Friday and Saturday nights. VIP tables for regular nights should be booked at least 48 hours ahead. For special events and guest DJ nights, we recommend booking 1-2 weeks in advance as these sell out quickly."
    },
    {
      question: "What is the cancellation policy for VIP table bookings?",
      answer: "We require 48 hours notice for cancellations to receive a full refund. Cancellations within 24-48 hours receive 50% refund. Cancellations within 24 hours are non-refundable. We understand plans change, so contact us as soon as possible if you need to modify your booking."
    }
  ];

  const breadcrumbItems = [
    { name: 'VIP Table Booking in Jamshedpur', url: null }
  ];

  const vipBenefits = [
    "Priority entry - skip the regular queue",
    "Best seating with prime views of DJ and dance floor",
    "Dedicated server for your entire night",
    "Premium bottle service from our extensive menu",
    "Complimentary mixers, juices, and garnishes",
    "Personalized service and attention",
    "Reserved seating area for your entire group",
    "Photography opportunities with VIP backdrop"
  ];

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <SEOHead
        title="VIP Table Booking Jamshedpur | Reserve Premium Tables at Scratch Unleashed"
        description="Book VIP tables at Scratch Unleashed, Jamshedpur's premier nightclub. Priority entry, bottle service, best views. Perfect for celebrations & special nights!"
        keywords="VIP table booking Jamshedpur, VIP nightclub Jamshedpur, bottle service Jamshedpur, premium table booking, VIP booth Jamshedpur, nightclub reservation Jamshedpur"
        canonical="https://scratchjsr.com/vip-table-booking-jamshedpur"
      />
      <FAQSchema faqs={faqs} />
      <BreadcrumbSchema items={[
        { name: 'Home', url: 'https://scratchjsr.com/' },
        { name: 'VIP Table Booking in Jamshedpur', url: 'https://scratchjsr.com/vip-table-booking-jamshedpur' }
      ]} />

      <div className="h-16"></div>

      <div className="max-w-[1120px] mx-auto px-4 sm:px-6 py-12">
        <Breadcrumbs items={breadcrumbItems} />

        {/* Hero Section */}
        <div className="mb-12">
          <h1 className="font-bebas text-5xl sm:text-6xl lg:text-7xl mb-4">
            VIP TABLE BOOKING IN JAMSHEDPUR
          </h1>
          <p className="text-xl text-[var(--text-dim)] mb-6">
            Experience Nightlife Like Never Before
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button className="btn-crimson-glow rounded-xl" onClick={() => navigate('/checkin')}>
              Reserve VIP Table
              <ArrowRight size={16} className="ml-2" />
            </Button>
            <Button variant="outline" className="rounded-xl border-[var(--border-custom)]" onClick={() => navigate('/#vip')}>
              View VIP Packages
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-8">
            {/* Introduction */}
            <section>
              <h2 className="font-bebas text-3xl mb-4">Premium VIP Table Experience in Jamshedpur</h2>
              <div className="space-y-4 text-[var(--text-dim)] leading-relaxed">
                <p>
                  When you're looking for the ultimate nightlife experience, nothing compares to <strong>VIP table booking at Scratch Unleashed</strong>. As Jamshedpur's premier nightclub, we've perfected the art of VIP service, creating an experience that transforms a regular night out into something truly special. From the moment you arrive to the last song of the night, VIP table service ensures you experience the best our venue has to offer.
                </p>
                <p>
                  What makes <strong>VIP table booking in Jamshedpur</strong> at Scratch Unleashed different? It's the combination of location, service, and exclusivity. Your VIP table isn't just a place to sit—it's your command center for the evening, positioned in prime locations with perfect sightlines to the DJ booth and dance floor. You're close enough to feel the energy of the crowd and the music, but you have your own private sanctuary where you can relax, celebrate, and enjoy premium service throughout the night.
                </p>
              </div>
            </section>

            {/* Why Book VIP */}
            <section>
              <h2 className="font-bebas text-3xl mb-4">Why Choose VIP Table Service?</h2>
              <div className="space-y-4 text-[var(--text-dim)] leading-relaxed">
                <p>
                  There's a significant difference between general entry and VIP table service at Scratch Unleashed. While both give you access to the same incredible music, lighting, and atmosphere, VIP table booking elevates every aspect of your experience.
                </p>
                <p>
                  <strong>Skip the Line:</strong> VIP table guests enjoy priority entry. No standing in line, no uncertainty about getting in during peak hours. You arrive, check in with our host, and you're escorted directly to your table. This alone saves valuable time and sets the tone for your entire evening.
                </p>
                <p>
                  <strong>Prime Location:</strong> VIP tables are positioned in the best spots in the venue. Whether you prefer being close to the DJ booth to feel the music's full impact or overlooking the dance floor to watch the crowd's energy, we have VIP placements that cater to different preferences. Standard entry means wherever you can find space—VIP means the best seat in the house is already reserved with your name on it.
                </p>
                <p>
                  <strong>Personalized Service:</strong> Each VIP table is assigned a dedicated server who remains attentive to your group throughout the night. Need another round? Want to order a different bottle? Have a special request? Your server handles everything, allowing you to focus entirely on enjoying the evening with your friends.
                </p>
                <p>
                  <strong>Premium Bottle Service:</strong> VIP table booking includes access to our full premium bottle menu. Instead of waiting at a crowded bar, your drinks arrive at your table in style—premium spirits, craft mixers, fresh garnishes, all presented and served with attention to detail.
                </p>
              </div>
            </section>

            {/* Table Options */}
            <section>
              <h2 className="font-bebas text-3xl mb-4">VIP Table Options & Tiers</h2>
              <div className="space-y-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-lg text-[var(--text)]">Standard VIP Tables</h3>
                    <Badge className="text-xs">4-6 People</Badge>
                  </div>
                  <p className="text-[var(--text-dim)] mb-3">
                    Perfect for small groups wanting the VIP experience. Located around the main floor with excellent views. Includes dedicated server, bottle service, and priority entry. Minimum spend starting from ₹20,000. Ideal for regular weekend nights and smaller celebrations.
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-lg text-[var(--text)]">Premium VIP Booths</h3>
                    <Badge variant="outline" className="text-xs border-[var(--accent-gold)] text-[var(--accent-gold)]">8-12 People</Badge>
                  </div>
                  <p className="text-[var(--text-dim)] mb-3">
                    Step up to our enclosed VIP booths offering more privacy and space. Features plush seating, enhanced bottle service presentation, and prime positioning. Perfect for birthday celebrations, corporate outings, and larger groups. Minimum spend from ₹35,000. These book up quickly for special event nights.
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-lg text-[var(--text)]">Stage-Side Tables</h3>
                    <Badge className="text-xs bg-[var(--accent-gold)] text-black">Ultimate Experience</Badge>
                  </div>
                  <p className="text-[var(--text-dim)] mb-3">
                    The pinnacle of VIP experiences. Positioned right next to the DJ booth, these tables put you in the heart of the action. Feel every beat, watch the DJ work, and be part of the energy that drives the entire venue. Includes all premium services plus custom bottle presentations and dedicated photography. Minimum spend from ₹50,000. Extremely limited availability.
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-lg text-[var(--text)]">Exclusive Lounge Area</h3>
                    <Badge className="text-xs">15-20 People</Badge>
                  </div>
                  <p className="text-[var(--text-dim)] mb-3">
                    For larger groups and special celebrations, our exclusive lounge can be reserved for complete privacy while still being connected to the main energy of the club. Features multiple seating configurations, private bartender, enhanced sound system, and customizable lighting. Perfect for corporate events, large birthday parties, or bachelor/bachelorette celebrations. Custom pricing based on requirements.
                  </p>
                </div>
              </div>
            </section>

            {/* The Booking Process */}
            <section>
              <h2 className="font-bebas text-3xl mb-4">How VIP Table Booking Works</h2>
              <div className="space-y-4 text-[var(--text-dim)] leading-relaxed">
                <p>
                  Booking a <strong>VIP table at Scratch Unleashed</strong> is straightforward. Here's what you can expect:
                </p>
                <p>
                  <strong>1. Select Your Date & Table:</strong> Contact our reservations team via our website, phone, or in person. Let us know your preferred date, group size, and any special requirements. We'll present available table options based on your needs.
                </p>
                <p>
                  <strong>2. Confirm Your Reservation:</strong> Once you've selected your table and agreed to the minimum spend, we'll confirm your reservation. For special events and peak nights, we require an advance deposit (credited toward your minimum spend).
                </p>
                <p>
                  <strong>3. Arrival & Check-In:</strong> On your event night, arrive at Scratch Unleashed and check in at our VIP entrance. Our host will verify your reservation and escort you directly to your table, bypassing any regular entry lines. We're open Friday-Sunday from 6 PM onwards. For weekday private events, arrival time is coordinated with your booking.
                </p>
                <p>
                  <strong>4. Enjoy Premium Service:</strong> Your dedicated server will introduce themselves, present the bottle menu, and take your initial order. Throughout the night, they'll ensure your table is maintained, drinks are served, and any special requests are handled.
                </p>
                <p>
                  <strong>5. Flexible Spending:</strong> Your minimum spend can be allocated however you prefer—bottles, premium cocktails, food items (when available), or additional services. At the end of the night, any amount exceeding your minimum spend is settled via card or digital payment.
                </p>
              </div>
            </section>

            {/* What's Included */}
            <section>
              <h2 className="font-bebas text-3xl mb-4">What's Included with Every VIP Table</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {vipBenefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <Check size={18} className="text-[var(--accent-gold)] mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-[var(--text-dim)]">{benefit}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Call to Action */}
            <section className="bg-[var(--surface)] border border-[var(--border-custom)] rounded-2xl p-8">
              <h2 className="font-bebas text-3xl mb-4">Ready to Reserve Your VIP Table?</h2>
              <p className="text-[var(--text-dim)] mb-6">
                Elevate your next night out with VIP table service at Scratch Unleashed. Whether you're celebrating a special occasion or simply want to experience Jamshedpur's best nightlife in style, our VIP tables deliver an unforgettable experience. Book your table today.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button className="btn-crimson-glow rounded-xl" onClick={() => navigate('/checkin')}>
                  Book VIP Table Now
                </Button>
                <Button variant="outline" className="rounded-xl border-[var(--border-custom)]" onClick={() => navigate('/birthday-party-venue-jamshedpur')}>
                  Birthday Party Packages
                </Button>
              </div>
            </section>

            {/* FAQ Section */}
            <section>
              <h2 className="font-bebas text-3xl mb-6">Frequently Asked Questions</h2>
              <Accordion type="single" collapsible className="space-y-3">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`} className="border border-[var(--border-custom)] rounded-xl px-4 bg-[var(--surface)]">
                    <AccordionTrigger className="hover:no-underline text-left">
                      <span className="font-semibold">{faq.question}</span>
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-[var(--text-dim)] pb-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Quick Pricing */}
            <Card className="p-6 bg-[var(--surface)] border border-[var(--border-custom)] rounded-xl">
              <h3 className="font-bebas text-2xl mb-4">Starting Prices</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm">Standard VIP</span>
                  <span className="font-semibold text-[var(--accent-gold)]">₹20,000</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Premium Booth</span>
                  <span className="font-semibold text-[var(--accent-gold)]">₹35,000</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Stage-Side</span>
                  <span className="font-semibold text-[var(--accent-gold)]">₹50,000</span>
                </div>
                <p className="text-xs text-[var(--text-muted)] pt-2 border-t border-[var(--border-custom)]">
                  Minimum spend. Final amount varies by event and date.
                </p>
              </div>
            </Card>

            {/* VIP Perks */}
            <Card className="p-6 bg-[var(--surface)] border border-[var(--border-custom)] rounded-xl">
              <h3 className="font-bebas text-2xl mb-4">VIP Perks</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Crown size={20} className="text-[var(--accent-gold)] mt-1" />
                  <div>
                    <p className="font-semibold text-sm">Priority Access</p>
                    <p className="text-sm text-[var(--text-dim)]">Skip the line completely</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Star size={20} className="text-[var(--accent-gold)] mt-1" />
                  <div>
                    <p className="font-semibold text-sm">Best Views</p>
                    <p className="text-sm text-[var(--text-dim)]">Prime positioning guaranteed</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Wine size={20} className="text-[var(--accent-gold)] mt-1" />
                  <div>
                    <p className="font-semibold text-sm">Bottle Service</p>
                    <p className="text-sm text-[var(--text-dim)]">Premium spirits & mixers</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Users size={20} className="text-[var(--accent-gold)] mt-1" />
                  <div>
                    <p className="font-semibold text-sm">Dedicated Server</p>
                    <p className="text-sm text-[var(--text-dim)]">Personalized attention all night</p>
                  </div>
                </div>
              </div>
            </Card>


            {/* Latest Articles */}
            <LatestArticles maxPosts={3} />

            {/* Related Pages */}
            <Card className="p-6 bg-[var(--surface)] border border-[var(--border-custom)] rounded-xl">
              <h3 className="font-bebas text-2xl mb-4">Explore More</h3>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/night-club-in-jamshedpur')}
                  className="w-full text-left text-sm text-[var(--accent-gold)] hover:underline"
                >
                  → Night Club in Jamshedpur
                </button>
                <button
                  onClick={() => navigate('/dj-night-jamshedpur')}
                  className="w-full text-left text-sm text-[var(--accent-gold)] hover:underline"
                >
                  → DJ Nights
                </button>
                <button
                  onClick={() => navigate('/birthday-party-venue-jamshedpur')}
                  className="w-full text-left text-sm text-[var(--accent-gold)] hover:underline"
                >
                  → Birthday Party Venue
                </button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
